import { faSort, faSortDown, faSortUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames/bind";
import ObjectUtils, { coalesce, sanitizeNoAllowedTags } from "../utils/ObjectUtils";

const SEMESTER_TYPE_PROGRAMM_NAME = "Lektorenprogramm";
const PROJECT_CATEGORY_SAP = "SAP";
const PROGRAMS_WITH_PROJECT_FOREIGN_ID = ["EPOS", "SPDL", "Lektorenprogramm"];
const FUNDING_AREAS_WITH_PROJECT_FOREIGN_ID = ["Aktivitätenmonitoring"];

export default class SurveyUtils {
  /**
   * Returns the object which represents ordered list of templates
   *
   * @param templateList
   * @param sortTableTemplateBy
   * @param sortTableTemplateAscending
   * @returns Object which represents ordered list of templates
   */
  static handleSortTemplateList(templateList, sortTableTemplateBy, sortTableTemplateAscending) {
    let sortedTemplateList = templateList.slice(0);

    if (
      sortTableTemplateBy === "lastModifiedDate" ||
      sortTableTemplateBy === "lastEdit" ||
      sortTableTemplateBy === "start"
    ) {
      sortedTemplateList.sort(function (a, b) {
        if (sortTableTemplateAscending) {
          return a[sortTableTemplateBy] - b[sortTableTemplateBy];
        } else {
          return -a[sortTableTemplateBy] + b[sortTableTemplateBy];
        }
      });
    } else {
      sortedTemplateList.sort(function (a, b) {
        let x;
        let y;
        if (sortTableTemplateBy === "projectForeignId") {
          x = a[sortTableTemplateBy];
          y = b[sortTableTemplateBy];
        } else {
          x =
            sortTableTemplateBy === "language"
              ? a[sortTableTemplateBy]
                ? a[sortTableTemplateBy]["name"].toLowerCase()
                : a["languageId"].toLowerCase()
              : a[sortTableTemplateBy]
              ? a[sortTableTemplateBy].toLowerCase()
              : "zzz";
          y =
            sortTableTemplateBy === "language"
              ? b[sortTableTemplateBy]
                ? b[sortTableTemplateBy]["name"].toLowerCase()
                : b["languageId"].toLowerCase()
              : b[sortTableTemplateBy]
              ? b[sortTableTemplateBy].toLowerCase()
              : "zzz";
        }

        if (sortTableTemplateAscending) {
          return x < y ? -1 : x > y ? 1 : 0;
        } else {
          return x < y ? 1 : x > y ? -1 : 0;
        }
      });
    }

    return sortedTemplateList;
  }

  /**
   * Returns the object which represents ordered list of questions
   *
   * @param questionList
   * @param sortTableBy
   * @param subType
   * @param sortAscending
   * @returns Object which represents ordered list of templates
   */
  static handleSortQuestionLibrary(questionList, sortTableBy, subType, sortAscending, language) {
    let sortedQuestionList = questionList.slice(0);

    sortedQuestionList.sort(function (a, b) {
      let j =
        a[sortTableBy] && a[sortTableBy][subType] ? a[sortTableBy][subType] : a[sortTableBy] ? a[sortTableBy] : "";
      let i =
        b[sortTableBy] && b[sortTableBy][subType] ? b[sortTableBy][subType] : b[sortTableBy] ? b[sortTableBy] : "";
      if (a[sortTableBy]) {
        let x = language && j[language] ? coalesce(j[language].toLowerCase(), "") : coalesce(j.toLowerCase(), "");
        let y = language && i[language] ? coalesce(i[language].toLowerCase(), "") : coalesce(i.toLowerCase(), "");
        if (sortAscending) {
          return x < y ? -1 : x > y ? 1 : 0;
        } else {
          return x < y ? 1 : x > y ? -1 : 0;
        }
      }
      return 0;
    });

    return sortedQuestionList;
  }

  /**
   * Returns the object which represents ordered list of indicators -  first founding areas then program indicators
   *
   * @param indicatorList
   * @returns Object which represents ordered list of indicators
   */
  static handleSortIndicators(indicatorList) {
    let sortedIndicatorList = indicatorList.slice(0);
    sortedIndicatorList.sort(function (a, b) {
      let x = a.shortName.toLowerCase();
      let y = b.shortName.toLowerCase();
      if (a.program) {
        y = "";
      }
      if (b.program) {
        x = "";
      }
      if (a.program && b.program) {
        x = a.shortName.toLowerCase();
        y = b.shortName.toLowerCase();
      }
      return x < y ? -1 : x > y ? 1 : 0;
    });

    return sortedIndicatorList;
  }

  /**
   *
   * Adds correspondent class for table-body cell
   */
  static addClassForTableColumns(column, sortTableTemplateBy, ...more) {
    const colName = ObjectUtils.toKebabCase(column);
    return classNames(
      {
        "text-truncate": true,
        sorted: sortTableTemplateBy === column,
        [colName]: !!colName
      },
      more
    );
  }

  /**
   *
   * Adds correspondent class for table-header cell
   */
  static addClassForTableHeader(column, sortTableTemplateBy, ...more) {
    const colName = ObjectUtils.toKebabCase(column);
    return classNames(
      "text-nowrap",
      {
        sorted: sortTableTemplateBy === column,
        [colName]: !!colName
      },
      more
    );
  }

  static sortIcon(column, sortTableTemplateBy, sortTableTemplateAscending) {
    return (
      <>
        <FontAwesomeIcon
          className="ms-2"
          icon={sortTableTemplateBy === column ? (sortTableTemplateAscending ? faSortUp : faSortDown) : faSort}
          size="xs"
        />
      </>
    );
  }

  static isTextChanged(oldText, newText) {
    return this.normalizeTextForComparison(oldText) !== this.normalizeTextForComparison(newText);
  }

  static normalizeTextForComparison(text) {
    return sanitizeNoAllowedTags(text)
      .replace(/\s+/g, " ")
      .trim();
  }

  static isSemesterTypeProgram(programName) {
    return programName === SEMESTER_TYPE_PROGRAMM_NAME;
  }

  static hasProjectForeignId(overview) {
    if (!overview) {
      return false;
    }

    if (overview.projectCategory === PROJECT_CATEGORY_SAP) {
      return true;
    }

    const projectForeignId = overview.projectForeignId;
    const hasProjectForeignId =
      typeof projectForeignId === "string" ? projectForeignId.trim().length > 0 : !!projectForeignId;
    const program = overview.program;
    const fundingArea = overview.fundingArea;


    return hasProjectForeignId && (PROGRAMS_WITH_PROJECT_FOREIGN_ID.includes(program) || FUNDING_AREAS_WITH_PROJECT_FOREIGN_ID.includes(fundingArea));
  }

  static getReportPeriodLabel(intl, programName, year) {
    return this.isSemesterTypeProgram(programName) ?
      `${intl.formatMessage({id: 'respondents.overview.period.name'})} ${year}/${String(Number(year) + 1).slice(-2)}` :
      year

  }
}
